<script setup lang="ts">

import { CarbucksLogo } from '@app/Components/CarbucksLogo';

import VehicleForm from "../Components/VehicleForm.vue";
import LicensePlate from "../Components/LicensePlate.vue";
import { computed } from 'vue';
import { useWelcomeFlowStore } from '../WelcomeFlowStore';
import { storeToRefs } from 'pinia';

const welcomeFlowStore = useWelcomeFlowStore();
const vehicle = storeToRefs( welcomeFlowStore ).vehicle;

const findButtonDisabled = computed( () => ( vehicle.value.rego ?? '' ).length < 2 || !vehicle.value.regoState?.trim() );

</script>

<template>
    <div class="flex flex-col min-h-full pt-4 overflow-y-auto">

        <!-- LOGO -->
        <!-- <div class="shrink-0 text-center transition-all duration-300 ease-in
                    pt-6 opacity-100 overflow-hidden -translate-y-2"
             :class="{
                'mb-4': ( welcomeFlowStore.hasSearchedForVehicle && !welcomeFlowStore.hasMatch ) || !welcomeFlowStore.hasSearchedForVehicle,
                'mb-2': welcomeFlowStore.hasMatch
             }" >
            <div class="flex flex-col items-center">
                <CarbucksLogo :size="96" />
                <div class="text-lg text-center px-4 tracking-wider
                            transition-all duration-300 ease-in max-h-48 overflow-hidden"
                     :class="{ '!max-h-0': welcomeFlowStore.hasMatch }">
                    Keeps your car <strong class="uppercase text-primary-500">healthy</strong>
                    <br/>
                    and your wallet <strong class="uppercase text-primary-500">happy</strong>   
                </div>
            </div>
        </div> -->

        <div class="grow w-full flex flex-col items-center transition-all duration-300 ease-in pb-8 pt-8 2xl:pt-32">

            <!-- LABELS -->
            <div class="w-full text-center text-white"
                 :class="{
                    'mb-6': welcomeFlowStore.hasSearchedForVehicle
                 }">

                <div class="text-center top-0 w-full px-4 2xl:px-8
                            transition-[opacity,transform] duration-300 ease-in max-h-0 overflow-hidden opacity-0 translate-y-4"
                     :class="{
                        'max-h-40 opacity-100 !translate-y-0': welcomeFlowStore.hasSearchedForVehicle
                     }">

                    <h1 v-if="welcomeFlowStore.hasMatch" class="text-base pt-4 mb-4">
                       Congratulations!<br/>We found your car
                    </h1>

                    <h1 v-else class="text-base pt-4 mb-4">
                       Welcome to Carbucks!
                    </h1>

                    <p v-if="welcomeFlowStore.vehicleDisplayName"
                       class="vehicle-name-animation my-2 w-full
                              px-3 py-1 3xl:py-3 text-2xl 3xl:text-4xl
                              uppercase font-bold">
                       {{ welcomeFlowStore.vehicleDisplayName }}
                    </p>

                </div>

                <!-- LICENSE PLATE -->
                <LicensePlate />

                <!-- GET THE USER TO ENTER THEIR REGO -->
                <div v-if="!welcomeFlowStore.hasSearchedForVehicle"
                     class="text-sm px-4 mt-6 mb-4">
                    Start by entering your car's rego and rego state!
                </div>
            </div>

            <!-- VEHICLE DETAILS FORM -->
            <VehicleForm 
                class="flex-grow">

                <template #actions="{ form }">

                    <p v-if="welcomeFlowStore.hasMatch" 
                       class="text-white/60 text-base text-center 
                               px-4 py-4">
                        Please check that the details are correct before continuing
                    </p>

                    <div class="w-full flex flex-col px-4 2xl:px-8">

                        <!-- The user has not searched for a vehicle yet -->
                        <Button v-if="!welcomeFlowStore.hasSearchedForVehicle"
                                :disabled="findButtonDisabled"
                                variant="tonal"
                                :style="{ 'background-color': findButtonDisabled ? 'rgba( 255, 255, 255, 0.05 ) !important' : ''  }"
                                size="large"
                                class="w-full !h-11 lg:!h-14 min-w-56 transition-all duration-300"
                                @click="welcomeFlowStore.getVehicleDetailsAsync()">
                            Find my car
                            <Icon name="chevron-right" class="ml-2 text-2xl" />
                        </Button>

                        <!-- The user has searched for a vehicle -->
                        <Button v-else-if="welcomeFlowStore.hasSearchedForVehicle"
                                variant="tonal"
                                size="large"
                                type="submit"
                                class="w-full mt-4 !h-12 lg:!h-14"
                                :class="{ 'opacity-30': form.isInvalid }"
                                :readonly="form.isInvalid"
                                @click="() => welcomeFlowStore.claimVehicleAsync()">
                             {{ welcomeFlowStore.hasMatch ? "That's my car, let's go" : "Let's go" }}
                            <Icon name="chevron-right" class="side-to-side ml-2 text-2xl" />
                        </Button>
                        
                        <!-- The user has searched for a vehicle but thinks the details are incorrect and needs to reset the form -->
                        <Button v-if="welcomeFlowStore.hasSearchedForVehicle"
                                variant="text"
                                color="white"
                                size="large"
                                class="w-full !h-12 lg:!h-14 mt-1"
                                @click="welcomeFlowStore.resetVehicleSearch()">
                            {{ welcomeFlowStore.vehicle.make ? "That's not my car" : "Try again" }}
                        </Button>
                    </div>

                </template>

            </VehicleForm>

        </div>

        <div class="shrink-0 text-white/20 text-xs 3xl:text-base text-center px-4 lg:px-6 pt-8 pb-4
                    transition-all duration-300 ease-in"
            :class="{
                'translate-y-4 !opacity-0 max-h-0 hidden': welcomeFlowStore.hasMatch || welcomeFlowStore.vehicleIsNotFound
            }">
            *This app is available exclusively for the benefit of customers of Australian automotive dealerships.  Contact your approved dealership for access
        </div>
    
    </div>

</template>

<style scoped lang="scss">

    @keyframes vehicle-name-keyframes {
        0%,100% { background-position: 0 0; }
        50% { background-position: 100% 0; }
    }

    .vehicle-name-animation 
    {
        background: linear-gradient(to right, #6666ff, #0099ff, #00ff00, #ffe100, #ff5500, #933bff, #6666ff );
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        animation: vehicle-name-keyframes 5s ease-in-out infinite alternate;
        filter: brightness( 120% );
        background-size: 400% 100%;
    }

</style>